import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Play, Trash2 } from 'lucide-react';
import api from '../api';
import { Breadcrumbs } from './Breadcrumbs';

function SyncManagement({ user, onLogout }) {
  const navigate = useNavigate();
  const [connections, setConnections] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [connectionsData, logsData] = await Promise.all([
        api.getSyncConnections(),
        api.getSyncLogs(),
      ]);
      setConnections(connectionsData);
      setLogs(logsData);
    } catch (error) {
      console.error('Failed to load sync data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateConnection = async (connectionData) => {
    try {
      const newConnection = await api.createSyncConnection(connectionData);
      setConnections([...connections, newConnection]);
      setShowCreateModal(false);
    } catch (error) {
      console.error('Failed to create connection:', error);
      alert('Failed to create connection');
    }
  };

  const handleTriggerSync = async (connectionId, direction) => {
    try {
      await api.triggerSync(connectionId, direction);
      alert('Sync started! Check logs for progress.');
      setTimeout(loadData, 2000); // Reload after 2 seconds
    } catch (error) {
      console.error('Failed to trigger sync:', error);
      alert('Failed to trigger sync');
    }
  };

  return (
    <div className="min-h-full">
      {/* Breadcrumbs */}
      <div className="px-6 lg:px-8 pt-6 pb-2">
        <Breadcrumbs items={[
          { label: 'Dashboard', path: '/' },
          { label: 'Integrations', path: '/sync' },
        ]} />
      </div>

      {/* Header */}
      <div className="px-6 lg:px-8 pt-4 pb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Integrations</h1>
            <p className="text-sm text-muted-foreground mt-1">Configure Jira and Clarity connections</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-5 py-2.5 bg-primary text-primary-foreground rounded-xl hover:opacity-90 font-semibold flex items-center gap-2 shadow-sm shadow-primary/20 btn-press transition-opacity"
            data-testid="add-connection-button"
          >
            <Plus className="h-4 w-4" />
            Add Connection
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 lg:px-8 pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Connections */}
          <div>
            <h2 className="text-lg font-semibold mb-4">Connections</h2>
            {loading ? (
              <div className="space-y-3">
                {[1, 2].map((i) => (
                  <div key={i} className="h-24 bg-muted rounded-lg skeleton"></div>
                ))}
              </div>
            ) : connections.length === 0 ? (
              <div className="text-center py-12 bg-card border border-border rounded-lg">
                <p className="text-muted-foreground mb-4">No connections configured yet</p>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                >
                  Add First Connection
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {connections.map((conn) => (
                  <div
                    key={conn._id}
                    className="p-4 bg-card border border-border rounded-lg"
                    data-testid={`connection-${conn._id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-semibold">{conn.name}</h3>
                        <p className="text-sm text-muted-foreground capitalize">{conn.system}</p>
                      </div>
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          conn.sync_status === 'success'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                            : conn.sync_status === 'error'
                            ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400'
                        }`}
                      >
                        {conn.sync_status}
                      </span>
                    </div>
                    {conn.last_sync && (
                      <p className="text-xs text-muted-foreground mb-3">
                        Last synced: {new Date(conn.last_sync).toLocaleString()}
                      </p>
                    )}
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleTriggerSync(conn._id, 'inbound')}
                        className="flex-1 px-3 py-1.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm flex items-center justify-center gap-1"
                        data-testid={`sync-inbound-${conn._id}`}
                      >
                        <Play className="h-3 w-3" />
                        Sync Inbound
                      </button>
                      <button
                        onClick={() => handleTriggerSync(conn._id, 'outbound')}
                        className="flex-1 px-3 py-1.5 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 transition-colors text-sm flex items-center justify-center gap-1"
                        data-testid={`sync-outbound-${conn._id}`}
                      >
                        <Play className="h-3 w-3" />
                        Sync Outbound
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Sync Logs */}
          <div>
            <h2 className="text-lg font-semibold mb-4">Sync Logs</h2>
            {logs.length === 0 ? (
              <div className="text-center py-12 bg-card border border-border rounded-lg">
                <p className="text-muted-foreground">No sync logs yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {logs.map((log) => (
                  <div
                    key={log._id}
                    className="p-4 bg-card border border-border rounded-lg"
                    data-testid={`log-${log._id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span
                        className={`text-sm font-medium capitalize ${
                          log.status === 'failed' ? 'text-destructive' : ''
                        }`}
                      >
                        {log.status}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(log.started_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      Direction: {log.direction} • {log.items_synced} synced, {log.items_failed} failed
                    </p>
                    {log.log_details && log.log_details.length > 0 && (
                      <div className="mt-2 p-2 bg-muted rounded text-xs font-mono">
                        {log.log_details.map((detail, i) => (
                          <div key={i}>{detail}</div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Connection Modal */}
      {showCreateModal && (
        <CreateConnectionModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateConnection}
        />
      )}
    </div>
  );
}

function CreateConnectionModal({ onClose, onCreate }) {
  const [formData, setFormData] = useState({
    name: '',
    system: 'jira',
    config: {
      url: '',
      username: '',
      api_token: '',
      project_key: '',
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onCreate(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="create-connection-modal">
      <div className="bg-card p-6 rounded-lg max-w-md w-full mx-4 border border-border">
        <h2 className="text-xl font-bold mb-4">Add Sync Connection</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="e.g., Production Jira"
              className="w-full px-3 py-2 border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              data-testid="connection-name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">System</label>
            <select
              value={formData.system}
              onChange={(e) => setFormData({ ...formData, system: e.target.value })}
              className="w-full px-3 py-2 border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              data-testid="connection-system"
            >
              <option value="jira">Jira</option>
              <option value="clarity">Clarity PPM</option>
            </select>
          </div>

          <div className="p-3 bg-amber-100 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700 rounded-md">
            <p className="text-xs text-amber-800 dark:text-amber-200">
              <strong>Note:</strong> This is a mocked integration. Sync operations will simulate data
              transfer but won't connect to real {formData.system.toUpperCase()} instances.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">URL</label>
            <input
              type="url"
              value={formData.config.url}
              onChange={(e) =>
                setFormData({ ...formData, config: { ...formData.config, url: e.target.value } })
              }
              placeholder={formData.system === 'jira' ? 'https://your-domain.atlassian.net' : 'https://clarity-host.com'}
              className="w-full px-3 py-2 border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              data-testid="connection-url"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Username/Email</label>
            <input
              type="text"
              value={formData.config.username}
              onChange={(e) =>
                setFormData({ ...formData, config: { ...formData.config, username: e.target.value } })
              }
              placeholder="user@example.com"
              className="w-full px-3 py-2 border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              data-testid="connection-username"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">API Token</label>
            <input
              type="password"
              value={formData.config.api_token}
              onChange={(e) =>
                setFormData({ ...formData, config: { ...formData.config, api_token: e.target.value } })
              }
              placeholder="Your API token"
              className="w-full px-3 py-2 border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              data-testid="connection-token"
            />
          </div>

          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
              data-testid="submit-connection"
            >
              Create Connection
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SyncManagement;
